import type { ProviderDefinitionDto, ProviderView } from "./api";

type ProviderLike = Pick<
  ProviderView,
  "connectionMode" | "credentials" | "keyCount" | "keyLabels" | "quota"
>;

/* ── Health / status ───────────────────────────────────────────── */

export type HealthTone = "ok" | "warn" | "danger" | "muted";

export function healthDisplay(status: ProviderView["connectionStatus"]): {
  label: string;
  tone: HealthTone;
} {
  switch (status) {
    case "healthy":
      return { label: "Healthy", tone: "ok" };
    case "connected":
      return { label: "Connected", tone: "ok" };
    case "verifying":
      return { label: "Verifying", tone: "warn" };
    case "connecting":
      return { label: "Connecting", tone: "warn" };
    case "degraded":
      return { label: "Degraded", tone: "warn" };
    case "quota_exhausted":
      return { label: "Quota Exhausted", tone: "danger" };
    case "unhealthy":
      return { label: "Offline", tone: "danger" };
    case "error":
      return { label: "Error", tone: "danger" };
    case "disabled":
      return { label: "Disabled", tone: "muted" };
    default:
      return { label: "Needs Setup", tone: "muted" };
  }
}

/** Provider needs operator attention (degraded/error/exhausted/missing key). */
export function needsAttention(p: ProviderView): boolean {
  if (["degraded", "unhealthy", "quota_exhausted", "error"].includes(p.connectionStatus))
    return true;
  return p.credentials?.state === "missing";
}

/* ── Credentials ───────────────────────────────────────────────── */

/** Honest credential summary — never "missing" for keyless-capable providers. */
export function credentialsLabel(p: ProviderLike): string {
  if (p.credentials) {
    switch (p.credentials.state) {
      case "anonymous":
        return "Anonymous";
      case "none_required":
        return "No key required";
      case "missing":
        return "Setup required";
      case "configured":
        return `${p.keyCount || 1} API key${(p.keyCount || 1) === 1 ? "" : "s"}`;
    }
  }
  if ((p.keyCount ?? 0) > 0) return `${p.keyCount} API key${p.keyCount === 1 ? "" : "s"}`;
  if (p.connectionMode === "anonymous") return "Anonymous";
  return "No keys";
}

/* ── Quota ─────────────────────────────────────────────────────── */

const QUOTA_SOURCE_LABEL: Record<string, string> = {
  official_docs: "Documented limits",
  provider_managed: "Provider managed",
  provider_api: "Provider managed",
  provider_dashboard: "Provider managed",
  response_headers: "Live headers",
  observed: "Observed limits",
  unknown: "Unknown",
};

/** Humanized one-line quota summary — never raw metadata strings. */
export function quotaLabel(p: ProviderLike): string {
  const q = p.quota;
  if (!q) return "—";
  if (q.remainingRatio != null) return `${Math.round(q.remainingRatio * 100)}% remaining`;
  if (q.note) return "Limited"; // details live in the drawer — not on cards
  return QUOTA_SOURCE_LABEL[q.source] ?? "Provider managed";
}

/** Technical quota detail for the Advanced disclosure in the drawer. */
export function quotaDetail(p: ProviderLike): string[] {
  const q = p.quota;
  if (!q) return [];
  const out: string[] = [];
  if (q.note) out.push(q.note);
  for (const l of q.limits) {
    out.push(`${l.kind} · ${l.window}: ${l.remaining}/${l.limit} remaining (${l.source})`);
  }
  if (!out.length) out.push(`Source: ${q.source}`);
  return out;
}

/* ── Capability chips ──────────────────────────────────────────── */

const CAP_LABEL: Record<string, string> = {
  chat: "Chat",
  streaming: "Streaming",
  tool_calling: "Tools",
  image_understanding: "Vision",
  embeddings: "Embeddings",
  structured_output: "JSON",
  model_listing: "Discovery",
  image_generation: "Images",
  audio: "Audio",
};

export function capChips(caps: string[], max = 4): string[] {
  const out: string[] = [];
  for (const c of caps) {
    if (CAP_LABEL[c]) out.push(CAP_LABEL[c]);
    if (out.length >= max) break;
  }
  return out;
}

/* ── Free-access badges ────────────────────────────────────────── */

const FREE_BADGE: Record<string, string> = {
  native_free_tier: "FREE TIER",
  free_plan: "FREE PLAN",
  free_mode: "FREE MODE",
  free_models: "FREE MODELS",
  free_endpoints: "FREE ENDPOINTS",
  free_daily_credits: "DAILY FREE",
  free_monthly_credits: "MONTHLY FREE",
  free_trial_credits: "FREE TRIAL",
  anonymous_free: "ANONYMOUS FREE",
  free_token: "FREE TOKEN",
  time_limited_free: "TIME-LIMITED",
  evaluation_only: "EVAL ONLY",
  paid_only: "PAID",
  retired: "RETIRED",
};

export function freeBadge(def: Pick<ProviderDefinitionDto, "freeAccess">): string | null {
  const types = def.freeAccess?.types ?? [];
  for (const t of types) {
    if (FREE_BADGE[t] && t !== "paid_only") return FREE_BADGE[t]!;
  }
  if (types.includes("paid_only")) return "PAID";
  return null;
}

export function isNoKey(def: Pick<ProviderDefinitionDto, "freeAccess">): boolean {
  const k = def.freeAccess?.keyRequired;
  return k === "none" || k === "optional";
}

export function isFreeCapable(def: Pick<ProviderDefinitionDto, "freeAccess">): boolean {
  const types = def.freeAccess?.types ?? [];
  return types.some((t) => t !== "paid_only" && t !== "unknown" && t !== "retired");
}

/** "API key required" / "No API key required" / "Key + config required". */
export function requirementLabel(def: ProviderDefinitionDto): string {
  const k = def.freeAccess?.keyRequired ?? "required";
  if (k === "none") return "No API key required";
  if (k === "optional") return "No key needed — optional key raises limits";
  const extras = def.credentialFields.filter(
    (f) => f.required && f.key !== "apiKey" && f.key !== "apiToken",
  );
  if (k === "conditional")
    return extras.length ? "Conditional credentials" : "No key needed";
  return extras.length
    ? `API key + ${extras.map((f) => f.label).join(" + ")}`
    : "API key required";
}

/* ── Search / filter / sort ────────────────────────────────────── */

/** Query matches name, description, capabilities, free status, key mode. */
export function matchesQuery(
  hay: {
    name: string;
    shortDescription?: string;
    description?: string;
    capabilities?: string[];
    features?: string[];
    freeAccess?: { types?: string[]; keyRequired?: string } | null;
    category?: string;
  },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const capLabels = (hay.capabilities ?? []).map((c) => CAP_LABEL[c] ?? c);
  const freeTypes = hay.freeAccess?.types ?? [];
  const fields = [
    hay.name,
    hay.shortDescription ?? "",
    hay.description ?? "",
    hay.category ?? "",
    ...capLabels,
    ...(hay.features ?? []),
    ...freeTypes.map((t) => FREE_BADGE[t] ?? t.replace(/_/g, " ")),
  ];
  const keyMode = hay.freeAccess?.keyRequired;
  if (keyMode === "optional" || keyMode === "none") {
    fields.push("no key", "anonymous", "keyless", "free");
  }
  if (keyMode === "required") fields.push("api key", "byok");
  if (freeTypes.includes("anonymous_free")) fields.push("no key", "anonymous");
  const blob = fields.join(" ").toLowerCase();
  return q.split(/\s+/).every((tok) => blob.includes(tok));
}

export type ProviderFilter =
  | "all"
  | "connected"
  | "free"
  | "nokey"
  | "attention"
  | "byok";

export type ProviderSort =
  | "recommended"
  | "health"
  | "freemodels"
  | "latency"
  | "recent";

export function filterDefinition(
  d: ProviderDefinitionDto,
  f: ProviderFilter,
): boolean {
  switch (f) {
    case "all":
    case "connected":
      return true; // "connected" is meaningless for catalog defs — show all
    case "free":
      return isFreeCapable(d);
    case "nokey":
      return isNoKey(d);
    case "attention":
      return true;
    case "byok":
      return !isNoKey(d);
  }
}

export function filterProviderView(p: ProviderView, f: ProviderFilter): boolean {
  switch (f) {
    case "all":
      return true;
    case "connected":
      return p.connectionStatus !== "not_connected";
    case "free":
      return (
        p.freeModelCount > 0 ||
        Boolean(
          p.definition.freeAccess?.types?.some(
            (t) => t !== "paid_only" && t !== "unknown" && t !== "retired",
          ),
        )
      );
    case "nokey":
      return (
        p.connectionMode === "anonymous" ||
        p.definition.freeAccess?.keyRequired === "none" ||
        p.definition.freeAccess?.keyRequired === "optional"
      );
    case "attention":
      return needsAttention(p);
    case "byok":
      return p.keyCount > 0 || p.definition.freeAccess?.keyRequired === "required";
  }
}

const HEALTH_RANK: Record<string, number> = {
  healthy: 0,
  connected: 1,
  verifying: 2,
  connecting: 2,
  degraded: 3,
  quota_exhausted: 4,
  unhealthy: 5,
  error: 6,
  disabled: 7,
  not_connected: 8,
};

export function sortProviderViews(
  list: ProviderView[],
  sort: ProviderSort,
): ProviderView[] {
  const copy = [...list];
  switch (sort) {
    case "health":
      return copy.sort(
        (a, b) =>
          (HEALTH_RANK[a.connectionStatus] ?? 9) -
          (HEALTH_RANK[b.connectionStatus] ?? 9),
      );
    case "freemodels":
      return copy.sort((a, b) => b.freeModelCount - a.freeModelCount);
    case "latency":
      return copy.sort(
        (a, b) =>
          (a.health?.p50LatencyMs ?? Number.MAX_SAFE_INTEGER) -
          (b.health?.p50LatencyMs ?? Number.MAX_SAFE_INTEGER),
      );
    case "recent":
      return copy.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    default:
      // recommended: healthy+free first, then priority-ish by name
      return copy.sort(
        (a, b) =>
          (HEALTH_RANK[a.connectionStatus] ?? 9) -
            (HEALTH_RANK[b.connectionStatus] ?? 9) ||
          b.freeModelCount - a.freeModelCount,
      );
  }
}

export function sortDefinitions(
  list: ProviderDefinitionDto[],
  sort: ProviderSort,
): ProviderDefinitionDto[] {
  const copy = [...list];
  if (sort === "recommended") {
    // no-key + free first, then alphabetical — catalog order is priority-ranked
    return copy.sort(
      (a, b) =>
        Number(isNoKey(b)) - Number(isNoKey(a)) ||
        Number(isFreeCapable(b)) - Number(isFreeCapable(a)),
    );
  }
  return copy;
}

/* ── Free-pool summary ─────────────────────────────────────────── */

export interface FreePoolSummary {
  freeModels: number;
  sources: number;
  byCapability: Array<{ label: string; count: number }>;
}

export function summarizeFreePool(
  providers: ProviderView[],
  freeModels: Array<{ capabilities?: Record<string, boolean>; scores?: Record<string, number> }>,
): FreePoolSummary {
  const sources = providers.filter(
    (p) => p.freeModelCount > 0 && p.connectionStatus !== "disabled",
  ).length;
  const count = (fn: (c: Record<string, boolean>) => boolean) =>
    freeModels.filter((m) => m.capabilities && fn(m.capabilities)).length;
  const coding = freeModels.filter(
    (m) => (m.scores?.coding ?? 0) >= 0.7,
  ).length;
  return {
    freeModels: freeModels.length,
    sources,
    byCapability: [
      { label: "Chat", count: count((c) => Boolean(c.chat)) },
      { label: "Coding", count: coding },
      { label: "Vision", count: count((c) => Boolean(c.vision)) },
      { label: "Tools", count: count((c) => Boolean(c.toolCalling)) },
      { label: "Embeddings", count: count((c) => Boolean(c.embeddings)) },
      { label: "Images", count: count((c) => Boolean(c.imageGeneration)) },
    ].filter((c) => c.count > 0),
  };
}
